import { useRef, useState, type ChangeEvent } from "react";
import { GUILDS } from "../../data/seed";
import { demoMode, supabase } from "../../lib/supabase";
import { usePortal } from "../../state/PortalContext";
import { Icon } from "../../components/Icon";
import { Button, Card, Pill, ProgressBar, SectionHeading } from "../../components/ui";

export function ProfilePage() {
  const { user, progress, switchGuild, updateProfile } = usePortal();
  const [name, setName] = useState(user?.fullName ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  if (!user) return null;

  const uploadAvatar = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || file.size > 2_000_000) return;
    if (!demoMode && supabase) {
      const extension = file.name.split(".").pop()?.toLowerCase() ?? "webp";
      const path = `${user.id}/avatar-${Date.now()}.${extension}`;
      const { error } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      await updateProfile({ avatarUrl: data.publicUrl });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => void updateProfile({ avatarUrl: String(reader.result) });
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-7">
      <SectionHeading
        eyebrow="Character Sheet"
        title="Your adventurer profile"
        detail="Customize your identity, choose a guild path, and review the achievements earned across the world."
      />

      <div className="grid gap-6 xl:grid-cols-[.72fr_1.28fr]">
        <Card className="p-7 text-center">
          <button
            onClick={() => inputRef.current?.click()}
            className="group relative mx-auto grid h-32 w-32 place-items-center overflow-hidden rounded-[2rem] border-4 border-indigo-400/50 bg-gradient-to-br from-indigo-500 to-violet-700 text-white shadow-glow"
            aria-label="Upload avatar"
          >
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <Icon name="User" size={54} />
            )}
            <span className="absolute inset-x-0 bottom-0 bg-slate-950/85 py-2 text-[10px] font-bold opacity-0 transition group-hover:opacity-100 group-focus:opacity-100">
              Change avatar
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => void uploadAvatar(event)}
            className="hidden"
          />
          <Pill tone="amber" className="mt-5">
            Level {user.level}
          </Pill>
          <h2 className="mt-3 font-display text-3xl font-bold text-white">
            {user.fullName}
          </h2>
          <p className="mt-1 text-sm text-slate-500">{user.email}</p>
          <div className="mt-6 text-left">
            <ProgressBar
              value={(user.xp / user.xpNext) * 100}
              label={`${user.xp} / ${user.xpNext} XP`}
            />
          </div>
          <div className="mt-7 border-t border-slate-800 pt-6 text-left">
            <label htmlFor="display-name" className="text-xs font-bold text-slate-300">
              Display name
            </label>
            <input
              id="display-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
            />
            <Button
              className="mt-3 w-full"
              onClick={() => void updateProfile({ fullName: name.trim() || user.fullName })}
            >
              <Icon name="Save" size={17} />
              Save profile
            </Button>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-7">
            <p className="font-retro text-[8px] tracking-widest text-cyan-300">
              GUILD MATRIX
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-white">
              Choose the route that fits you now
            </h2>
            <p className="mt-2 text-sm text-slate-400">
              Switching changes recommendations and pathways. Your completed
              quests and competency evidence stay with you.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {GUILDS.map((guild) => (
                <button
                  key={guild.id}
                  onClick={() => void switchGuild(guild.id)}
                  className={`rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 ${
                    user.guild === guild.id
                      ? "border-indigo-400/60 bg-indigo-500/10"
                      : "border-slate-800 bg-slate-950/45 hover:border-slate-600"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: guild.color }}
                    />
                    {user.guild === guild.id && (
                      <Icon name="Check" size={17} className="text-emerald-300" />
                    )}
                  </div>
                  <p className="mt-3 font-display text-lg font-bold text-white">
                    {guild.name}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    {guild.description}
                  </p>
                  <p className="mt-3 text-[10px] font-bold text-slate-400">
                    {user.guildReputation[guild.id]} reputation
                  </p>
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-7">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-retro text-[8px] tracking-widest text-amber-300">
                  TROPHY CABINET
                </p>
                <h2 className="mt-2 font-display text-2xl font-bold text-white">
                  {progress.badges.length} badges earned
                </h2>
              </div>
              <Icon name="Trophy" size={28} className="text-amber-300" />
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {progress.badges.map((badge) => (
                <div
                  key={badge.id}
                  className="flex gap-4 rounded-2xl border border-slate-800 bg-slate-950/45 p-4"
                >
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-amber-400/10 text-amber-300">
                    <Icon name={badge.icon} size={21} />
                  </span>
                  <div>
                    <p className="font-bold text-slate-200">{badge.name}</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {badge.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
